<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState">
      <a-form-item v-if="user?.customer?.type_business == 1" label="Empresa" name="entity" required>
        <entity-picker
          :showAll="false"
          defaultBehavior
          :value="formState.entity"
          @update:value="formState.entity = $event"
        />
      </a-form-item>

      <a-form-item label="Campaña" name="portfolio" required>
        <campaign-picker
          :showAll="false"
          :value="formState.portfolio"
          @update:value="formState.portfolio = $event"
          :entity="formState.entity"
        />
      </a-form-item>

      <a-form-item v-if="user?.customer?.enable_bases" label="Base" name="base" required>
        <bases-picker
          defaultBehavior
          :value="formState.base"
          @update:value="formState.base = $event"
          :entity="formState.entity"
          :portfolio="formState.portfolio"
        />
      </a-form-item>

      <a-form-item label="Usuarios" name="user">
        <SelectorUsers defaultBehavior v-model:value="formState.user" />
      </a-form-item>

      <a-form-item label="Fecha de inicio de PDP" name="startDate" required>
        <a-date-picker
          v-model:value="formState.startDate"
          format="DD/MM/YYYY"
          valueFormat="YYYY-MM-DD"
          placeholder="-- / -- / ----"
        />
      </a-form-item>

      <a-form-item label="Fecha de fin de PDP" name="endDate" required>
        <a-date-picker
          v-model:value="formState.endDate"
          format="DD/MM/YYYY"
          valueFormat="YYYY-MM-DD"
          placeholder="-- / -- / ----"
        />
      </a-form-item>
    </a-form>

    <div class="actions">
      <span v-if="totalRecords !== null" class="total">{{ totalRecords }} Registros</span>

      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <a-table
    size="middle"
    id="inbound-table"
    :scroll="scrollStyle"
    :columns="listColumns"
    :data-source="reportData?.results"
    :loading="isTableLoading"
    :pagination="pagination"
    @change="handleTableChange"
  />
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons-vue";

import type { Form, Report } from "@/@types/reports/payPromise/list";
import type { UserData } from "@/@types/global";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { TABLE_PAGE_SIZE } from "@/app/shared/utils/constants";
import { fetchList, downloadList } from "@/app/reports/services";
import { listColumns } from "@/app/reports/utils/tableColumns/payPromise/listColumns";

import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import BasesPicker from "@/app/shared/components/pickers/BasesPicker.vue";
import SelectorUsers from "@/app/shared/components/selectors/SelectorUsers.vue";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";

const store = useStore();

const user = computed<UserData>(() => store.getters["getDataUser"]);
const { scrollStyle } = useScrollResize({ headerMargin: 420 });

// Form
const formState = reactive<Form>({
  portfolio: null,
  user: "",
  startDate: null,
  endDate: null,
});
const totalRecords = ref<number | null>(null);
const isButtonDisabled = computed(() => {
  for (const key in formState) {
    if (formState[key as keyof Form] === null) return true;
  }

  return false;
});
const isDownloading = ref(false);
const { isGlobalDownloading } = useGlobalDownloadState();

// Table
const reportData = ref<Report | null>(null);
const currentTablePage = ref(1);
const isTableLoading = ref(false);

const pagination = computed(() => ({
  total: reportData.value?.count,
  current: currentTablePage.value,
  pageSize: TABLE_PAGE_SIZE,
  showSizeChanger: false,
}));

const handleTableChange = async (page: { pageSize: number; current: number }) => {
  currentTablePage.value = page.current;
  await fetchReportData();
};

const fetchReportData = async () => {
  isTableLoading.value = true;

  const response = await fetchList({ filters: formState, page: currentTablePage.value });
  reportData.value = response;
  totalRecords.value = reportData.value?.count ?? 0;

  isTableLoading.value = false;
};

const handleSearch = async () => {
  currentTablePage.value = 1;
  await fetchReportData();
};

const handleDownload = async () => {
  isDownloading.value = true;
  await downloadList({ filters: formState });
  isDownloading.value = false;
};
</script>

<style lang="sass" scoped>
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 180px

:deep(.ant-form-item-label)
  padding: 0

.actions
  display: flex
  align-items: center
  gap: 10px

#inbound-table tfoot th,
#inbound-table tfoot td
  background-color: #fafafa !important
</style>
